import AddIcon from '@mui/icons-material/Add';
import { Box, Chip } from '@mui/material';
import { Text } from 'components/Text';
import { getTrans } from 'modules/i18n';

/**
 * Tappable suggestions for the free-text list steps (soft skills, certificates).
 *
 * Both steps already ship a curated option list, but it only appeared inside the
 * autocomplete's dropdown — so a talent who didn't think to open the menu saw an empty box
 * and typed nothing. Every chip-selection screen we benchmarked (Upwork, Bumble,
 * MyFitnessPal, Deepstash) puts the suggestions on the screen and treats typing as the
 * fallback rather than the path.
 *
 * Already-selected options are filtered out, so the cloud shrinks as the talent picks.
 */
export const SuggestionChips = ({
  label,
  options,
  selected,
  onAdd,
  max
}: {
  label: string;
  options: readonly string[];
  selected: string[];
  onAdd: (option: string) => void;
  /** Cap suggestions shown at once so the cloud can't dominate the step. */
  max?: number;
}) => {
  const remaining = options.filter(option => !selected.includes(option));
  const visible = max ? remaining.slice(0, max) : remaining;

  if (visible.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Text variant="caption" sx={{ display: 'block', mb: 1 }}>
        {label}
      </Text>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {visible.map(option => (
          <Chip
            key={option}
            label={option}
            icon={<AddIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            onClick={() => onAdd(option)}
            data-testid={`suggestion-chip-${option}`}
            data-dd-action-name="cv-suggestion-add"
            sx={theme => ({
              cursor: 'pointer',
              borderColor: theme.palette.divider,
              '&:hover': { backgroundColor: theme.palette.action.hover }
            })}
          />
        ))}
      </Box>
    </Box>
  );
};

/** "3 selected" — a live count, so the talent can tell whether they've done enough. */
export const SelectedCount = ({ count }: { count: number }) => (
  <Text variant="caption" data-testid="selected-count">
    {count} {getTrans('cvBuilderPage.selection.selected')}
  </Text>
);
